import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api from '../utils/api';
import { Alert, Button, Container, Field, Input, Panel } from '../components/site/ui';
import { IconCheckCircle, IconLock } from '../components/icons';
import SiteCredit from '../components/SiteCredit';

/**
 * Handles both invitations and password resets — they differ only in wording,
 * so the page reads `purpose` off the token rather than duplicating a screen.
 */
const SetPasswordPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();

  const [state, setState] = useState({ loading: true, valid: false });
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(null);

  const inspect = useCallback(async () => {
    try {
      const { data } = await api.get(`/api/auth/password-token/${token}`);
      setState({ loading: false, valid: true, ...data });
    } catch (err) {
      setState({
        loading: false,
        valid: false,
        error: err.response?.data?.error || 'This link is invalid or has expired.'
      });
    }
  }, [token]);

  useEffect(() => {
    inspect();
  }, [inspect]);

  const loginPath = (role) => (role === 'admin' ? '/admin/login' : '/merchant/login');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Your password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('The two passwords do not match.');
      return;
    }

    setSaving(true);
    try {
      const { data } = await api.post('/api/auth/set-password', { token, password });
      setDone(data);
      // Give the confirmation a moment to be read before moving on.
      setTimeout(() => navigate(loginPath(data.role), { replace: true }), 2500);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not set your password.');
    } finally {
      setSaving(false);
    }
  };

  if (state.loading) {
    return (
      <Container width="sm" className="py-16">
        <p className="text-center text-sm text-ink-500">Checking your link…</p>
      </Container>
    );
  }

  if (done) {
    return (
      <Container width="sm" className="py-12 sm:py-20">
        <Panel className="text-center">
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-success-50 text-success-600 ring-1 ring-inset ring-success-100">
            <IconCheckCircle size={32} strokeWidth={1.5} />
          </span>
          <h1 className="mt-6 font-display text-2xl font-800 tracking-tight text-ink-900">
            {done.purpose === 'invite' ? 'Your account is ready' : 'Password updated'}
          </h1>
          <p className="mt-3 text-[15px] leading-relaxed text-ink-600">
            You can now sign in as <span className="font-600">{done.email}</span>. Taking you to the
            sign-in page…
          </p>
          <Button as={Link} to={loginPath(done.role)} size="lg" className="mt-7">
            Go to sign in
          </Button>
        </Panel>

        <SiteCredit className="mt-6 text-center" />
      </Container>
    );
  }

  if (!state.valid) {
    return (
      <Container width="sm" className="py-12 sm:py-20">
        <Panel>
          <h1 className="font-display text-xl font-700 tracking-tight text-ink-900">
            This link no longer works
          </h1>
          <p className="mt-2 text-[15px] leading-relaxed text-ink-600">{state.error}</p>
          <Alert tone="info" className="mt-5">
            Invitation and reset links can only be used once, and they expire. Ask for a new one and
            it will replace this link.
          </Alert>
          <div className="mt-6 flex flex-wrap gap-3">
            <Button as={Link} to="/forgot-password">
              Request a new link
            </Button>
            <Button as={Link} to="/merchant/login" variant="secondary">
              Back to sign in
            </Button>
          </div>
        </Panel>

        <SiteCredit className="mt-6 text-center" />
      </Container>
    );
  }

  const isInvite = state.purpose === 'invite';

  return (
    <Container width="sm" className="py-12 sm:py-16">
      <div className="mb-8">
        <Link to="/" className="inline-block w-fit">
          <img src="/brand.png" alt="ParcelBuddy" className="h-16 w-auto" />
        </Link>
      </div>

      <Panel>
        <h1 className="font-display text-xl font-700 tracking-tight text-ink-900">
          {isInvite ? `Welcome, ${state.name}` : 'Choose a new password'}
        </h1>
        <p className="mt-2 text-[15px] leading-relaxed text-ink-600">
          {isInvite
            ? 'Choose a password to activate your ParcelBuddy account.'
            : `Set a new password for ${state.email}.`}
        </p>

        <form onSubmit={handleSubmit} className="mt-6 space-y-5" noValidate>
          {error && <Alert tone="danger">{error}</Alert>}

          <Field label="New password" htmlFor="password" required hint="At least 8 characters.">
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                className="pr-16"
                required
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute inset-y-0 right-0 flex items-center px-3 text-xs font-600 text-ink-500 transition-colors hover:text-brand-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600"
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
          </Field>

          <Field label="Confirm password" htmlFor="confirm" required>
            <Input
              id="confirm"
              type={showPassword ? 'text' : 'password'}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              required
            />
          </Field>

          <Button type="submit" size="lg" loading={saving} className="w-full">
            {isInvite ? 'Activate my account' : 'Update password'}
          </Button>
        </form>

        <div className="mt-6 flex items-start gap-2 border-t border-ink-100 pt-5 text-xs text-ink-500">
          <IconLock size={14} className="mt-px shrink-0" />
          <p>This link can only be used once and expires automatically.</p>
        </div>
      </Panel>

      <SiteCredit className="mt-6 text-center" />
    </Container>
  );
};

export default SetPasswordPage;
